import {
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import VTCMenu from "../../web/components/VTCMenu";
import Breadcrumbs from "../../web/components/Breadcrumbs";
import { useContext, useEffect, useState } from "react";
import EnterpriseService from "../../common/services/enterprise-service";
import { EnterpriseContext } from "./EnterpriseContext";
import BoxDynamicAd from "../../ads/BoxDynamicAd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast, ToastContainer } from "react-toastify";
import LoadingOverlay from "react-loading-overlay";
import truckyService from "../../common/services/trucky-service";
import Separator from "../ui/Separator";
import CopyButton from "../ui/CopyButton";

export default function CompanyInvites({ container }) {
  const { globalContext } = useContext(EnterpriseContext);
  const [loadingData, setLoadingData] = useState(true);
  const [invites, setInvites] = useState([]);
  const es = new EnterpriseService();
  const [newInviteData, setNewInviteData] = useState({
    days_to_expire: 1, // Default to no expiration
  });

  const [newInvite, setNewInvite] = useState(null);

  useEffect(() => {
    truckyService.setDocumentTitle("Company Invites");
  }, []);

  if (!es.can(globalContext.member, "members.manage_applications")) {
    return;
  }

  useEffect(() => {
    getInvites();
  }, []);

  const getInvites = async () => {
    setLoadingData(true);
    try {
      const response = await es.getCompanyInvites(globalContext.company.id);
      setInvites(response);
    } catch (error) {
      console.error("Error fetching invites:", error);
    } finally {
      setLoadingData(false);
    }
  };

  const createInvite = async () => {
    setLoadingData(true);
    try {
      const response = await es.createCompanyInvite(
        globalContext.company.id,
        newInviteData
      );
      if (response.success) {
        toast.success("Invite created successfully!");
        setNewInvite(response.invite);
        getInvites();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error("Error creating invite:", error);
      toast.error("Failed to create invite. Please try again.");
    }
    setLoadingData(false);
  };

  const deleteInvite = async (id) => {
    setLoadingData(true);
    try {
      await es.deleteCompanyInvite(globalContext.company.id, id);
      toast.success("Invite deleted successfully!");
      getInvites();
    } catch (error) {
      console.error("Error deleting invite:", error);
      toast.error("Failed to delete invite. Please try again.");
    }
    setLoadingData(false);
  };

  return (
    <LoadingOverlay
      active={loadingData}
      spinner={
        <Spinner
          color="primary"
          style={{ width: "4rem", height: "4rem" }}
        ></Spinner>
      }
    >
      <Row noGutters className="" id="enterpriseContainer">
        <VTCMenu active={"/applications"} container={container}></VTCMenu>

        <Col>
          <Row noGutters>
            <Col>
              <Card className="h-100">
                <CardBody>
                  <Breadcrumbs
                    breadcrumbs={[
                      {
                        to: "/vtchub",
                        title: container.localeManager.strings.vtcHub,
                      },
                      {
                        to: "/company/members",
                        title: container.localeManager.strings.members,
                      },
                      {
                        to: "/applications",
                        title: container.localeManager.strings.applications,
                      },
                      {
                        title: "Invites",
                      },
                    ]}
                  ></Breadcrumbs>
                  <div className="d-flex justify-content-between">
                    <h5>Invites</h5>
                  </div>
                  <div className="my-5">
                    <FontAwesomeIcon icon="info-circle" className="me-2" />
                    Invites allow users to join your VTC without needing to
                    apply. Each invite can be used once and has an expiration
                    date. You can create multiple invites with different
                    expiration times.
                  </div>
                  <div className="d-flex">
                    <div className="d-flex align-items-center me-3">
                      <span className="w-100px">Expires at</span>
                      <InputGroup>
                        <Input
                          type="number"
                          min="0"
                          max="7"
                          value={newInviteData.days_to_expire || ""}
                          onChange={(e) =>
                            setNewInviteData({ days_to_expire: e.target.value })
                          }
                        ></Input>{" "}
                        <InputGroupText>
                          {container.localeManager.strings.days}
                        </InputGroupText>
                      </InputGroup>
                    </div>
                    <div className="d-flex align-items-center">
                      <Button color="primary" onClick={createInvite} size="sm">
                        Create new Invite
                      </Button>
                    </div>
                    <div className="d-flex align-items-center ms-3">
                      {newInvite && (
                        <>
                          <span className="text-success">
                            {newInvite.invite_url}
                          </span>
                          <CopyButton
                            text={`${window.location.host}/invite/${newInvite.invite_code}`}
                            className="ms-2"
                          />
                        </>
                      )}
                    </div>
                  </div>
                  <Separator></Separator>
                  {invites.length > 0 && (
                    <Table
                      responsive
                      striped
                      size="sm"
                      className="table-row-dashed table-row-gray-300 align-middle gs-0"
                      hover
                    >
                      <thead>
                        <tr>
                          <th>Code</th>
                          <th>Created By</th>
                          <th className="text-center">Status</th>
                          <th>Accepted by</th>
                          <th>Expires at</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {invites.map((invite) => (
                          <tr key={invite.invite_code}>
                            <td>
                              {invite.invite_code}
                              <CopyButton
                                text={invite.invite_url}
                                className="ms-2"
                              />
                            </td>
                            <td>{invite.user.name}</td>
                            <td className="text-center">
                              {invite.accepted ? (
                                <FontAwesomeIcon
                                  icon="check"
                                  className="text-success"
                                />
                              ) : (
                                <FontAwesomeIcon
                                  icon="clock"
                                  className="text-warning"
                                />
                              )}
                            </td>
                            <td>
                              {invite.accepted_by_user
                                ? invite.accepted_by_user.name
                                : ""}
                            </td>
                            <td>
                              {new Date(invite.expires_at) < new Date()
                                ? "Expired"
                                : new Date(invite.expires_at).toLocaleString()}
                            </td>
                            <td>
                              {!invite.accepted && (
                                <Button
                                  color="active-light-danger"
                                  className="btn-icon w-30px h-30px ms-auto"
                                  size="sm"
                                  onClick={() => deleteInvite(invite.id)}
                                >
                                  <FontAwesomeIcon icon="trash" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Col>
        <aside className="ad-column-placeholder">
          <BoxDynamicAd container={container} className="my-3" />
        </aside>
      </Row>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </LoadingOverlay>
  );
}
